import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, Stack, Link, Divider,
  Accordion, AccordionSummary, AccordionDetails, CircularProgress,
  InputAdornment, IconButton, FormControlLabel, Switch,
  Tabs, Tab, Chip,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as TestIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility, VisibilityOff,
  CheckCircle as CheckIcon,
  Email as EmailIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import api from '../services/api';
import PageHeader from '../components/PageHeader';

// Each PURPOSE represents a distinct mailbox the site can send from.
// Backend rule: when a purpose has no row of its own, EmailService
// falls back to the 'default' row.
const PURPOSES = [
  {
    key: 'default',
    label: 'Default / Ops',
    blurb: 'Used for auth, user invitations, and as the fallback when a product-specific mailbox is not configured.',
  },
  {
    key: 'screeners',
    label: 'Watermarked Screeners',
    blurb: 'Used for screener delivery links, invitations, and recipient notifications.',
  },
  {
    key: 'dcp',
    label: 'Theatrical DCP',
    blurb: 'Used for DCP delivery, KDM emails, venue cert invitations, and physical-drive notifications.',
  },
];

const PROVIDERS = {
  gmail: {
    label: 'Gmail',
    smtp_host: 'smtp.gmail.com', smtp_port: 587,
    smtp_use_tls: true, smtp_use_ssl: false,
    address_label: 'Gmail address',
    address_placeholder: 'notifications@yourcompany.com',
    address_helper: 'The Google account that will send mail',
    password_helper: '16-character app password from Google. NOT your normal Gmail password.',
    from_helper: "Defaults to the Gmail address above. To use a different address, it must be added as a 'Send mail as' alias on the Gmail account, otherwise Gmail will rewrite the From header back to the authenticated user.",
  },
  zoho: {
    label: 'Zoho Mail',
    smtp_host: 'smtp.zoho.com', smtp_port: 465,
    smtp_use_tls: false, smtp_use_ssl: true,
    address_label: 'Zoho mailbox address',
    address_placeholder: 'dcp@hollywoodhills.media',
    address_helper: 'The full Zoho mailbox that will send mail',
    password_helper: 'Application-Specific Password from Zoho. NOT your normal Zoho password.',
    from_helper: 'Defaults to the Zoho mailbox above. Zoho preserves the From header for any address on the same authenticated mailbox or verified domain alias.',
  },
  custom: {
    label: 'Custom SMTP',
    smtp_host: '', smtp_port: 587,
    smtp_use_tls: true, smtp_use_ssl: false,
    address_label: 'SMTP username',
    address_placeholder: 'usually the full email address',
    address_helper: 'Whatever your SMTP provider expects for AUTH LOGIN',
    password_helper: 'SMTP password or app-specific password from your provider',
    from_helper: 'The address recipients see in the From header. Some providers rewrite this if it does not match the authenticated user.',
  },
};

function detectProvider(smtp_host) {
  const h = (smtp_host || '').toLowerCase();
  if (h.includes('gmail')) return 'gmail';
  if (h.includes('zoho')) return 'zoho';
  return 'custom';
}

const emptyFormFor = (provider) => ({
  from_name: 'Screening Room',
  smtp_username: '',
  smtp_host: PROVIDERS[provider].smtp_host,
  smtp_port: PROVIDERS[provider].smtp_port,
  smtp_use_tls: PROVIDERS[provider].smtp_use_tls,
  smtp_use_ssl: PROVIDERS[provider].smtp_use_ssl,
  from_email: '',
  app_password: '',
  reply_to_email: '',
  bcc_enabled: false,
  bcc_addresses: '',
});

export default function EmailConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Which purpose tab is active. Defaults to 'default' so the operator
  // lands on the Ops mailbox first (it's the fallback for the others,
  // so it's the most consequential tab to keep correctly configured).
  const [purpose, setPurpose] = useState('default');
  // Map of purpose -> { provider, form, configured }
  const [byPurpose, setByPurpose] = useState(() => {
    const out = {};
    for (const p of PURPOSES) {
      out[p.key] = { provider: 'gmail', form: emptyFormFor('gmail'), configured: false };
    }
    return out;
  });

  const [testEmail, setTestEmail] = useState('');
  // Inline error/success specific to the test-send card. Separate from
  // the page-level alert because operators reported "400 Bad Request"
  // in DevTools and missed the diagnostic the alert was already showing.
  const [testResult, setTestResult] = useState(null);  // { kind: 'error'|'ok', message }

  const reloadAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/email/configs');
      const next = {};
      for (const p of PURPOSES) {
        const row = (data || []).find((r) => r.purpose === p.key);
        if (row) {
          const provider = detectProvider(row.smtp_host);
          next[p.key] = {
            provider,
            configured: true,
            form: {
              from_name: row.from_name || 'Screening Room',
              smtp_username: row.smtp_username || '',
              smtp_host: row.smtp_host || PROVIDERS[provider].smtp_host,
              smtp_port: row.smtp_port || PROVIDERS[provider].smtp_port,
              smtp_use_tls: !!row.smtp_use_tls,
              smtp_use_ssl: !!row.smtp_use_ssl,
              from_email: row.from_email || '',
              app_password: '',
              reply_to_email: row.reply_to_email || '',
              bcc_enabled: !!row.bcc_enabled,
              bcc_addresses: row.bcc_addresses || '',
            },
          };
        } else {
          next[p.key] = { provider: 'gmail', form: emptyFormFor('gmail'), configured: false };
        }
      }
      setByPurpose(next);
    } catch (_e) {
      // 404 / 403 etc.: leave defaults in place.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  const slot = byPurpose[purpose];
  const cfg = PROVIDERS[slot.provider];

  const setForm = (mut) => setByPurpose((bp) => ({
    ...bp,
    [purpose]: { ...bp[purpose], form: typeof mut === 'function' ? mut(bp[purpose].form) : mut },
  }));

  const setProvider = (next) => {
    if (!next || next === slot.provider) return;
    const p = PROVIDERS[next];
    setByPurpose((bp) => ({
      ...bp,
      [purpose]: {
        ...bp[purpose],
        provider: next,
        form: {
          ...bp[purpose].form,
          smtp_host: p.smtp_host || bp[purpose].form.smtp_host,
          smtp_port: p.smtp_port,
          smtp_use_tls: p.smtp_use_tls,
          smtp_use_ssl: p.smtp_use_ssl,
        },
      },
    }));
  };

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const save = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const f = slot.form;
      const payload = {
        purpose,
        smtp_host: f.smtp_host,
        smtp_port: parseInt(f.smtp_port, 10),
        smtp_use_tls: !!f.smtp_use_tls,
        smtp_use_ssl: !!f.smtp_use_ssl,
        smtp_username: f.smtp_username,
        from_email: f.from_email || f.smtp_username,
        from_name: f.from_name,
        reply_to_email: f.reply_to_email || null,
        send_to_recipient: true,
        send_to_sender: true,
        bcc_enabled: f.bcc_enabled,
        bcc_addresses: f.bcc_addresses || null,
      };
      if (f.app_password) payload.smtp_password = f.app_password.replace(/\s+/g, '');
      else if (!slot.configured) {
        setError('App password is required for the first save of this mailbox.');
        setSaving(false);
        return;
      }
      await api.post('/api/email/config', payload);
      setSuccess(`Saved ${PURPOSES.find((p) => p.key === purpose).label}.`);
      // Clear typed password from local state; reload to refresh metadata.
      setForm((f0) => ({ ...f0, app_password: '' }));
      await reloadAll();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail) {
      setTestResult({ kind: 'error', message: 'Enter a test recipient email first.' });
      return;
    }
    setTesting(true); setError(null); setSuccess(null); setTestResult(null);
    try {
      await api.post('/api/email/test-send', { test_email: testEmail, purpose });
      const okMsg = `Test email sent from the ${PURPOSES.find((p) => p.key === purpose).label} mailbox to ${testEmail}.`;
      setTestResult({ kind: 'ok', message: okMsg });
      setSuccess(okMsg);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Failed to send test email';
      setTestResult({ kind: 'error', message: detail });
      setError(detail);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const f = slot.form;
  const purposeMeta = PURPOSES.find((p) => p.key === purpose);

  return (
    <Box>
      <PageHeader
        title="Email"
        subtitle="One mailbox per product line. Each tab is independent; unconfigured tabs fall back to Default."
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={purpose}
          onChange={(_e, v) => setPurpose(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {PURPOSES.map((p) => (
            <Tab
              key={p.key}
              value={p.key}
              label={(
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                  {p.label}
                  {byPurpose[p.key]?.configured && (
                    <Chip
                      size="small"
                      color="success"
                      icon={<CheckIcon style={{ fontSize: 14 }} />}
                      label="Configured"
                      sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                    />
                  )}
                </Box>
              )}
            />
          ))}
        </Tabs>
        <Box sx={{ p: 2.5, display: 'flex', gap: 1.5, alignItems: 'flex-start', bgcolor: 'action.hover' }}>
          <InfoIcon fontSize="small" sx={{ mt: 0.25, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {purposeMeta.blurb}
            {!slot.configured && purpose !== 'default' && (
              <> Currently not configured, so outbound mail for this product line falls back to the <strong>Default / Ops</strong> mailbox.</>
            )}
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <EmailIcon color="primary" />
          <Typography variant="h6">{purposeMeta.label} mailbox</Typography>
          {slot.configured && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              <CheckIcon fontSize="small" color="success" />
              <Typography variant="caption" color="success.main">Configured</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Provider
          </Typography>
          <ToggleButtonGroup
            value={slot.provider}
            exclusive
            size="small"
            onChange={(_e, v) => setProvider(v)}
          >
            <ToggleButton value="gmail">Gmail</ToggleButton>
            <ToggleButton value="zoho">Zoho Mail</ToggleButton>
            <ToggleButton value="custom">Custom SMTP</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Stack spacing={2}>
          <TextField
            label="From name"
            value={f.from_name}
            onChange={update('from_name')}
            size="small"
            helperText="Shown as the sender in outgoing emails"
          />
          <TextField
            label={cfg.address_label}
            value={f.smtp_username}
            onChange={update('smtp_username')}
            size="small"
            type="email"
            placeholder={cfg.address_placeholder}
            helperText={cfg.address_helper}
          />
          <TextField
            label={slot.configured ? 'App password (leave blank to keep current)' : 'App password'}
            value={f.app_password}
            onChange={update('app_password')}
            size="small"
            type={showPassword ? 'text' : 'password'}
            placeholder="xxxx xxxx xxxx xxxx"
            helperText={cfg.password_helper}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="From address (what recipients see)"
            value={f.from_email}
            onChange={update('from_email')}
            size="small"
            type="email"
            placeholder="dcp@hollywoodhills.media"
            helperText={cfg.from_helper}
          />
          <TextField
            label="Reply-to (optional)"
            value={f.reply_to_email}
            onChange={update('reply_to_email')}
            size="small"
            type="email"
            helperText="Where replies should go, if different from the From address"
          />

          {slot.provider === 'custom' && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">SMTP server</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Host" value={f.smtp_host} onChange={update('smtp_host')}
                  size="small" placeholder="smtp.example.com" sx={{ flex: 2 }}
                />
                <TextField
                  label="Port" value={f.smtp_port} onChange={update('smtp_port')}
                  size="small" type="number" sx={{ flex: 1 }}
                />
              </Stack>
              <Stack direction="row" spacing={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={f.smtp_use_ssl}
                      onChange={(e) => setForm((f0) => ({ ...f0, smtp_use_ssl: e.target.checked, smtp_use_tls: e.target.checked ? false : f0.smtp_use_tls }))}
                    />
                  }
                  label="Use SSL (typically port 465)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={f.smtp_use_tls}
                      onChange={(e) => setForm((f0) => ({ ...f0, smtp_use_tls: e.target.checked, smtp_use_ssl: e.target.checked ? false : f0.smtp_use_ssl }))}
                    />
                  }
                  label="Use STARTTLS (typically port 587)"
                />
              </Stack>
            </>
          )}

          <Divider sx={{ my: 1 }} />
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={f.bcc_enabled}
                  onChange={(e) => setForm((f0) => ({ ...f0, bcc_enabled: e.target.checked }))}
                />
              }
              label="Silently BCC every outgoing email from this mailbox"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Useful for shadow-auditing all mail traffic. Recipients can't see the BCC list, the header is stripped before delivery.
            </Typography>
          </Box>
          <TextField
            label="BCC recipients (comma-separated)"
            value={f.bcc_addresses}
            onChange={update('bcc_addresses')}
            size="small"
            fullWidth
            disabled={!f.bcc_enabled}
            placeholder="ops@example.com, audit@example.com"
            helperText={f.bcc_enabled
              ? 'Each comma-separated address gets a copy of every email sent from this mailbox.'
              : 'Toggle on above to activate.'}
          />
          <Box>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={save}
              disabled={saving || !f.smtp_username || !f.from_name || !f.smtp_host}
            >
              {saving ? 'Saving...' : `Save ${purposeMeta.label}`}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Send a test email</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Verify the {purposeMeta.label} mailbox by sending a real test message from it.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <TextField
            size="small" type="email" label="Recipient"
            value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined" startIcon={<TestIcon />} onClick={sendTest}
            disabled={testing || !slot.configured}
          >
            {testing ? 'Sending...' : 'Send test'}
          </Button>
        </Stack>
        {!slot.configured && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Save the {purposeMeta.label} credentials first, then test.
          </Typography>
        )}
        {testResult && (
          <Alert
            severity={testResult.kind === 'ok' ? 'success' : 'error'}
            sx={{ mt: 2, whiteSpace: 'pre-line' }}
            onClose={() => setTestResult(null)}
          >
            {testResult.message}
          </Alert>
        )}
      </Paper>

      {slot.provider === 'zoho' && (
        <Accordion defaultExpanded={!slot.configured}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">How to get a Zoho application-specific password</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" component="div" sx={{ '& ol': { pl: 2.5 }, '& li': { mb: 1.5 } }}>
              <ol>
                <li>Sign in to the Zoho mailbox you want Screening Room to send from (e.g. <code>dcp@hollywoodhills.media</code>).</li>
                <li>Open <Link href="https://accounts.zoho.com/home#security/app_password" target="_blank" rel="noopener">Zoho Account → Security → App Passwords</Link>. Two-factor authentication on the account is required.</li>
                <li>Click <strong>Generate New Password</strong>. Name it something like &ldquo;Screening Room&rdquo;. Copy the password Zoho shows, you won&rsquo;t see it again. Spaces in the displayed value are optional, we strip them on save.</li>
                <li>Paste it into the <strong>App password</strong> field above, set the From name and Zoho mailbox address, then click <strong>Save</strong>.</li>
                <li>Send a test email to confirm the connection works.</li>
              </ol>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              <strong>SMTP details we use:</strong> <code>smtp.zoho.com:465</code> with SSL. If your Zoho region is EU, India, or Australia, switch to <strong>Custom SMTP</strong> and replace the host with <code>smtp.zoho.eu</code>, <code>smtp.zoho.in</code>, or <code>smtp.zoho.com.au</code>.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
      {slot.provider === 'gmail' && (
        <Accordion defaultExpanded={!slot.configured}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">How to get a Gmail app password</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" component="div" sx={{ '& ol': { pl: 2.5 }, '& li': { mb: 1.5 } }}>
              <ol>
                <li>Sign in to the Google account this mailbox uses.</li>
                <li>Make sure <strong>2-Step Verification</strong> is on. App passwords only exist on accounts with 2FA enabled. <Link href="https://myaccount.google.com/security" target="_blank" rel="noopener">Open Google Account → Security</Link>.</li>
                <li>Go to <Link href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener">https://myaccount.google.com/apppasswords</Link>.</li>
                <li>Under <strong>App name</strong>, type &ldquo;Screening Room&rdquo; and click <strong>Create</strong>.</li>
                <li>Google shows a 16-character password. Copy it, paste into the form above, and save.</li>
              </ol>
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
      {slot.provider === 'custom' && (
        <Accordion defaultExpanded={!slot.configured}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Custom SMTP notes</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary">
              Enter the host, port, and security mode your provider documents. Most providers accept either port 465 with SSL or port 587 with STARTTLS, not both at once. The username is usually the full email address you authenticate as. The password is either your provider account password or an app-specific token they generated for you.
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
}
